<template>
    <div v-if="hasSupportOptions" class="radio-group">
        <label class="radio-option rounded-start" :class="{'active': selectedNextStepsOption == 'buildPlan'}" data-value="buildPlan">
            <input type="radio" value="buildPlan" v-model="selectedNextStepsOption" />
            <div class="content">
                <i class="bi bi-tools"></i>
                <p class="fw-bold">I want to build a retirement plan</p>
                <p class="description">Click here to explore how your {{ scheme.name }} fits into your retirement plan.</p>
            </div>
        </label>
        <label class="radio-option rounded-end" :class="{'active': selectedNextStepsOption == 'getSupport'}" data-value="getSupport">
            <input type="radio" value="getSupport" v-model="selectedNextStepsOption" />
            <div class="content">
                <i class="bi bi-chat-right-text"></i>
                <p class="fw-bold">I need support with my options</p>
                <p class="description">Click here to view details of how you can get support with your options.</p>
            </div>
        </label>
    </div>

    <div class="row mt-4">
        <div class="col-12 mt-2">
            <div v-if="selectedNextStepsOption === 'buildPlan'" class="your-choice border-start border-success border-5 bg-light rounded-3 p-4 shadow-sm">
                <h4 class="mb-3">Build a retirement plan</h4>
                
                <p>The following option from the section above will be used to build your retirement plan in the rest of this tool:</p>
                <p class="fw-bold">{{ planChoicesText }}</p>

                <template v-if="isTakingTaxFreeCash">
                    <hr class="mt-4 pb-2 mx-5" />
                    <p class="fw-medium fst-italic">{{ text('dboptions.next-steps.tfc-spend-immediate.question') }}</p>
                    <p>{{ text('dboptions.next-steps.tfc-spend-immediate.advice') }}</p>

                    <div class="d-flex flex-wrap align-items-center">
                        <span class="me-4">
                            Immediate spend of tax free cash:
                            <span class="fw-bold" style="display: inline-block; width: 2ch; text-align: right;">
                                {{ formatter.formatCurrency(localImmediateSpendModel) }}
                            </span>
                        </span>
                        <div class="d-flex flex-grow-1 align-items-center mt-2 mt-sm-0">
                            <span class="ms-sm-4 me-3">0</span>
                            <input type="range"
                                class="form-range my-2 flex-grow-1"
                                v-model.number="localImmediateSpendModel"
                                min="0"
                                :max="taxFreeCashModelSupporter.activeActual"
                                step="100" />
                            <span class="ms-3">{{ formatter.formatCurrency(taxFreeCashModelSupporter.activeActual) }}</span>
                        </div>
                    </div>
                </template>

                <hr class="mt-4 mb-4 mx-5" />
                <p v-for="(p,i) of textArray('dboptions.next-steps.conclusion')" :key="i">{{ p }}</p>
                <small class="mt-4 fst-italic text-muted small">
                    Disclaimer: You should always seek appropriate professional advice and satisfy yourself that any action you take in connection with information 
                    and calculations provided by this website is suitable for your particular circumstances.
                </small>
            </div>
            <div v-else-if="selectedNextStepsOption === 'getSupport'" class="your-choice border-start border-success border-5 bg-light rounded-3 p-4 shadow-sm">
                <div v-if="schemeSupportMode === 'Coaching'">
                    <NextStepCoaching></NextStepCoaching>
                </div>
                <div v-else-if="schemeSupportMode === 'IFA'">
                    <NextStepIFA></NextStepIFA>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from "vue";
    import { formatter } from "@/shared/ts/formatter";
    import TaxFreeCashModelSupporter from "@/guiide/ts/assets/finalsalary/taxFreeCashModelSupporter";
    import FinalSalaryScheme from "@/guiide/ts/assets/finalsalary/finalSalaryScheme";
    import type { NextStepsOptions } from '@/guiide/ts/dbOptions/nextStepsOptions';
    import NextStepCoaching from "./NextStepCoaching.vue";
    import NextStepIFA from "./NextStepIFA.vue";
    import { useUIContent } from "@/guiide/ts/configuration/useUIContent";

    const selectedNextStepsOption = ref<NextStepsOptions>('buildPlan');

    const props = defineProps({
        scheme: { type: Object as () => FinalSalaryScheme, required: true },
        taxFreeCashModelSupporter: { type: TaxFreeCashModelSupporter, required: true },
        selectedOptionName: { type: String, required: true },
        immediateSpendModel: { type: Number, required: true },
        selectedOptionStartingPension: { type: Number, required: true }
    });

    const { text, textArray } = useUIContent();

    const schemeSupportMode = props.scheme.supportMode;

    const emit = defineEmits(["update-selected-next-steps-option", "update:immediateSpendModel"]);

    watch(selectedNextStepsOption, (newValue: NextStepsOptions) => {
        emit("update-selected-next-steps-option", newValue)
    }, { immediate: true });

    const hasSupportOptions = computed<boolean>(() => props.scheme.supportMode != "None");

    const localImmediateSpendModel = computed({
        get: () => props.immediateSpendModel,
        set: (value) => emit("update:immediateSpendModel", value)
    });

    const isTakingTaxFreeCash = computed<boolean>(() => props.taxFreeCashModelSupporter.activeActual > 0);

    const planChoicesText = computed(() => {
        const taxFreeCash = props.taxFreeCashModelSupporter.activeActual;
        const startingPension = ` - a starting pension of ${formatter.formatCurrency(props.selectedOptionStartingPension)}`;
        const taxFreeCashInfo = isTakingTaxFreeCash.value ? ` and a tax free cash lump sum of ${formatter.formatCurrency(taxFreeCash)}` : '';
        return props.selectedOptionName + startingPension + taxFreeCashInfo + '.';
    });
</script>

<style lang="css">
    .btn-secondary.btn-book {
        color: white !important;
    }

    .your-choice {
        border-left-width: 6px !important;
    }
</style>
